import {compact} from 'lodash';
import React, {Children, FC, PropsWithChildren} from 'react';
import styled from 'styled-components';

import {isElementOfType} from '../../../../../helpers/src/react/reactHelpers';
import {VisualSizesEnum} from '../../../styles/commonStyles';
import {makeSizeConstants} from '../../../styles/styleHelpers';
import {isSameWidthButton} from '../sameWidthButton';
import {ButtonGroupContextProps, ButtonGroupContextProvider} from './buttonGroupContext';
import {ButtonGroupSpacer} from './buttonGroupSpacer';

const spacings = makeSizeConstants(6, 10);

const defaultSpacingSize = VisualSizesEnum.MEDIUM;

/*
 * Props.
 */

type SpacingSize = VisualSizesEnum.SMALL | VisualSizesEnum.MEDIUM;

export interface ButtonGroupProps {
  className?: string;
  disableSameWidth?: boolean;
  // Determines the number of pixels between child elements/buttons. Defaults to MEDIUM if not specified.
  spacingSize?: SpacingSize;
}

/*
 * Styles.
 */

interface ButtonGroupStyleProps {
  $spacingSize?: SpacingSize;
}

const getSpacing = (p: ButtonGroupStyleProps): number => {
  const size = p.$spacingSize ?? defaultSpacingSize;
  return spacings[size];
};

const StyledGroupDiv = styled.div<ButtonGroupStyleProps>`
  display: flex;
  margin: 0 ${(p) => getSpacing(p) / -2 - 1}px;
`;

const StyledChildDiv = styled.div<ButtonGroupStyleProps>`
  margin: 0 ${(p) => getSpacing(p) / 2}px;
`;

/*
 * Component.
 */

/** Horizontal group of buttons that can interact with each other. */
export const ButtonGroup: FC<PropsWithChildren<ButtonGroupProps>> = ({
  disableSameWidth,
  children,
  className,
  spacingSize,
}) => {
  // Iterate on the children to list the sameWidthButton children.
  const allSameWidthChildren = !disableSameWidth
    ? Children.map(children, (child) => isSameWidthButton(child) && child.props.children)
    : [];

  // Build the context from this.
  const groupContext: ButtonGroupContextProps = {
    allSameWidthChildren: compact(allSameWidthChildren || []),
  };

  const allChildrenWithSpacing = Children.map(children, (child) =>
    !child || isElementOfType(child, ButtonGroupSpacer) ? (
      child
    ) : (
      <StyledChildDiv $spacingSize={spacingSize}>{child}</StyledChildDiv>
    ),
  );

  // Remove falsey children with spacing.
  const childrenWithSpacing = compact(allChildrenWithSpacing);

  return (
    <div className={className}>
      <StyledGroupDiv $spacingSize={spacingSize}>
        <ButtonGroupContextProvider value={groupContext}>{childrenWithSpacing}</ButtonGroupContextProvider>
      </StyledGroupDiv>
    </div>
  );
};
